// Shared fuzzy-search helpers for the admin dashboard.
//
// Two callers so far — donations transactions and the volunteers list —
// used to hand-roll their own `field.toLowerCase().includes(q)` chains,
// each missing a different set of visible fields and none of them
// Arabic-aware. Unified here so every list page searches the same way:
//
// - lowercased,
// - Arabic diacritics stripped,
// - alef variants (أ إ آ ٱ) collapsed to ا,
// - ى → ي, ة → ه (common typing/OCR variance),
// - every dash-like character (—, –, -, _) treated as a space,
// - whitespace collapsed.
//
// Query text is normalized the same way, then split into words; a row
// matches when its full "haystack" contains EVERY query word. Words can
// appear in any order, so "زكاه كفالات" matches "زكاة مال — كفالات" and
// "اسامه 5" matches "أسامة … 5 جنيه".

function normalize(input: string): string {
  return input
    .toLowerCase()
    .replace(/[ً-ْٰ]/g, '') // Arabic diacritics + dagger alef
    .replace(/[إأآٱ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ة/g, 'ه')
    .replace(/[—–\-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Splits query into normalized words. Empty query → empty list, which
// the callers interpret as "no search filter, match everything".
export function tokenizeQuery(q: string): string[] {
  const n = normalize(q);
  if (!n) return [];
  return n.split(' ').filter(Boolean);
}

// Builds one normalized haystack string from an array of fields. Nulls
// and undefineds are skipped. Numbers are coerced to strings so donors
// can search by amount.
export function buildHaystack(fields: Array<string | number | null | undefined>): string {
  return normalize(fields.filter((f) => f !== null && f !== undefined && f !== '').join(' '));
}

// Every token must appear somewhere in the haystack.
export function matchesAllTokens(haystack: string, tokens: string[]): boolean {
  if (tokens.length === 0) return true;
  for (const t of tokens) if (!haystack.includes(t)) return false;
  return true;
}
